import BASE_URL from '../_helpers/constants';
import axios from 'axios';
import { readCurrentUser } from "../_helpers/currentUser";
import { clearSession } from "../_helpers/session";
//signup
// Public, unauthenticated, and clients only. The server hard-codes
// `userType: "client"` and answers 400 to any body-supplied role (backend
// SEC-040), so never send one. Staff go through the two functions below.
export const registerUser = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/create`, body);

    return res
  } catch (error) {
    return error
  }
}

// Staff provisioning. Separate from registerUser because these routes are gated
// by `authMiddleware` and need the caller's token; registerUser sends none.
// The server assigns the role, so neither payload carries `userType`. Both
// answer 201 on success — callers must accept any 2xx, not just 200.

// Admin or super-admin may create an advisor.
export const createAdvisor = async (body) => {
  try {
    const token = JSON.parse(window.localStorage.getItem('currentUser')).token;
    const res = await axios.post(`${BASE_URL}/users/create-advisor`, body, {
      headers: {
        // `Bearer` is not optional: authMiddleware reads
        // `authorization.split(' ')[1]`, so a bare token is seen as absent (401).
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return res
  } catch (error) {
    return error
  }
}

// Super-admin only — a plain admin gets a 403 here (backend SEC-051).
export const createAdmin = async (body) => {
  try {
    const token = JSON.parse(window.localStorage.getItem('currentUser')).token;
    const res = await axios.post(`${BASE_URL}/users/create-admin`, body, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return res
  } catch (error) {
    return error
  }
}
//login
export const login = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/login`, body);
    return res
  } catch (error) {
    return error
  }
}

//forgot password
export const forgotPassword = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/generate-otp`, body);
    return res
  } catch (error) {
    return error
  }
}

//verify code
export const verificationCode = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/otp-verify`, body);
    return res
  } catch (error) {
    return error
  }
}

//verify code
export const resendVerificationCode = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/resend-otp`, body);
    return res
  } catch (error) {
    return error
  }
}

//reset password
export const resetPassword = async (body) => {
  try {
    let res = await axios.post(`${BASE_URL}/users/reset-password`, body);
    return res
  } catch (error) {
    return error
  }
}

// handle logout
//
// Backend SEC-115 (Astra-Backend#326) rewrote this route. It used to be
// unauthenticated, took a body `userId` and revoked nothing at all — it saved no
// state and cleared a cookie neither client reads. It now revokes the refresh
// token the caller presents, which means:
//
// - the credential is `{ refreshToken }`, not `{ userId }`. The account is the
//   token subject; a body userId is gone.
// - the access token goes in an `Authorization: Bearer` header. This route is one
//   of the few using `verifyToken`, which tolerates a bare token, but the prefix
//   is what the rest of the API expects and there is no reason to keep the odd
//   form here.
//
// Sending the old shape is why logging out stopped working: no `refreshToken`
// means a 400, and before the session pair was persisted at all the header was
// `undefined` and it was a 401 (#72).
//
// Local state is cleared **whatever the server says** — see clearSession (shared/_helpers/session.js).
// The caller only has to navigate.
export const logOut = async () => {
  const { token, refreshToken } = readCurrentUser() || {};

  try {
    // With no refresh token there is nothing for the server to revoke and the
    // request could only 400, so skip it and clear locally. Reachable for a
    // session persisted before the app stored the pair.
    if (!refreshToken) return { status: 200, skipped: true };

    return await axios.post(
      `${BASE_URL}/users/logout`,
      { refreshToken },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    return error;
  } finally {
    clearSession();
  }
}

// Revoke every session for this account, not just this device — bumps
// tokenVersion server-side, so outstanding access tokens die on their next
// request. The stolen-device response (SEC-115). No caller yet; here so the
// capability is not rediscovered from scratch.
export const logOutEverywhere = async () => {
  const { token } = readCurrentUser() || {};

  try {
    return await axios.post(
      `${BASE_URL}/users/logout-all`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    return error;
  } finally {
    clearSession();
  }
}

// change password
//
// Backend SEC-084 (Astra-Backend#308) split this off from the unauthenticated
// /reset-password route onto its own, authenticated one: {oldPassword,
// newPassword}, the account is always the Bearer token's subject (no email in
// the body), and oldPassword is mandatory. A change also revokes every session
// on the account, including this one (SEC-115's containment rule), so the
// success response carries a fresh {token, refreshToken} pair — the caller
// must persist it or its own next authenticated request 401s.
export const changePassword = async (oldPassword, newPassword) => {
  try {
    const token = JSON.parse(window.localStorage.getItem('currentUser')).token;
    const res = await axios.post(`${BASE_URL}/users/change-password`, {
      oldPassword, newPassword
    }, {
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return res;
  } catch (error) {
    return error;
  }
}

// quick setup
export const setQuickSetup = async (userId, quickSetupValue = true) => {
  try {
    const token = JSON.parse(window.localStorage.getItem('currentUser')).token;
    let res = await axios.post(`${BASE_URL}/users/quick-setup/${userId}`,{
      quickSetup: quickSetupValue
    }, {
      headers: {
        "Authorization": token,
        "Content-Type": "application/json",
      },
    })
    return res
  } catch (error) {
    return error;
  }
}

